export interface LearningPath {
  slug: string;
  name: string;
  industrySlug: string;
  lane: string;
  audience: string;
  why: string;
  courseSlugs: string[];
}

export const learningPaths: LearningPath[] = [
  {
    slug: "manufacturing-new-leader",
    name: "From Operator to Supervisor",
    industrySlug: "manufacturing",
    lane: "new-leader",
    audience: "Newly promoted frontline supervisors on the plant floor",
    why: "You know the line cold, but leading it is new; this path covers the basics before your first shift handoff goes sideways.",
    courseSlugs: [
      "new-leader-boot-camp",
      "adapting-your-leadership-style",
      "on-the-spot-development-conversations",
      "conquering-conflict",
      "driving-results-through-goal-setting",
      "creating-a-culture-of-trust",
    ],
  },
  {
    slug: "manufacturing-experienced-leader",
    name: "Running the Whole Plant",
    industrySlug: "manufacturing",
    lane: "experienced-leader",
    audience: "Area managers and senior operations leaders over multiple shifts",
    why: "When you lead supervisors instead of operators, takt time isn't the hard part anymore; this path builds judgment that spans shifts and departments.",
    courseSlugs: [
      "the-experienced-leader-boot-camp",
      "leading-leaders",
      "managing-organizational-conflict",
      "the-invincible-influencer",
      "financial-intelligence",
      "leading-change",
    ],
  },
  {
    slug: "manufacturing-communication-conflict",
    name: "Hard Talks on the Floor",
    industrySlug: "manufacturing",
    lane: "communication-conflict",
    audience: "Supervisors and leads who handle friction between shifts",
    why: "Missed handoffs and rework breed blame fast on a production floor; this path gives you the words for feedback, conflict, and pushback.",
    courseSlugs: [
      "emotional-triggers",
      "improve-communication-through-the-4p-personality-styles",
      "assertiveness-skills",
      "conquering-conflict",
      "strengthening-teams-through-communication",
    ],
  },
  {
    slug: "manufacturing-developing-people",
    name: "Build Your Bench",
    industrySlug: "manufacturing",
    lane: "developing-people",
    audience: "Plant leaders fighting turnover and thin skill coverage",
    why: "Every retirement on the floor takes decades of tribal knowledge with it; this path helps you coach, cross-train, and keep good operators from leaving.",
    courseSlugs: [
      "developing-others-your-top-priority",
      "collaborative-coaching-conversations",
      "delegating-for-development",
      "the-mentoring-mindset",
      "building-employee-engagement",
      "succession-planning-principles",
    ],
  },
  {
    slug: "manufacturing-safety-culture",
    name: "Speak Up About Safety",
    industrySlug: "manufacturing",
    lane: "custom",
    audience: "Supervisors and EHS leads responsible for shop-floor safety",
    why: "Near misses only get reported when people trust you won't punish them; this path builds the trust and habits that keep hazards visible.",
    courseSlugs: [
      "creating-a-culture-of-trust",
      "psychological-safety",
      "emotional-and-cognitive-empathy",
      "ethics-in-the-workplace",
    ],
  },
  {
    slug: "manufacturing-continuous-improvement",
    name: "Lead Continuous Improvement",
    industrySlug: "manufacturing",
    lane: "custom",
    audience: "Lean champions and leaders running improvement projects",
    why: "Kaizen ideas die when nobody owns follow-through; this path teaches you to find root causes, run the project, and get the floor behind changes.",
    courseSlugs: [
      "analytical-thinking-skills",
      "creative-problem-solving",
      "turn-your-ideas-into-results",
      "practical-project-management",
      "leading-change",
    ],
  },
  {
    slug: "healthcare-new-leader",
    name: "From Clinician to Charge",
    industrySlug: "healthcare",
    lane: "new-leader",
    audience: "New charge nurses and first-time clinical supervisors",
    why: "Great bedside care doesn't teach you how to run a unit; this path covers the basics before your first short-staffed shift tests you.",
    courseSlugs: [
      "new-leader-boot-camp",
      "leadership-essentials-with-a-foundation-of-emotional-intelligence",
      "adapting-your-leadership-style",
      "on-the-spot-development-conversations",
      "conquering-conflict",
      "balancing-priorities",
    ],
  },
  {
    slug: "healthcare-experienced-leader",
    name: "Leading Across Units",
    industrySlug: "healthcare",
    lane: "experienced-leader",
    audience: "Nurse managers, directors, and multi-department clinical leaders",
    why: "Census swings, travelers, and budget pressure land on you at once; this path sharpens the decisions that keep several units steady.",
    courseSlugs: [
      "the-experienced-leader-boot-camp",
      "leading-leaders",
      "managing-organizational-conflict",
      "financial-intelligence",
      "leading-change",
    ],
  },
  {
    slug: "healthcare-communication-conflict",
    name: "Hard Conversations at Handoff",
    industrySlug: "healthcare",
    lane: "communication-conflict",
    audience: "Charge nurses and clinical leads managing daily friction",
    why: "Handoff gaps and physician friction turn into patient risk quickly; this path gives you calm, direct language for feedback and conflict.",
    courseSlugs: [
      "emotional-triggers",
      "managing-emotions",
      "emotional-and-cognitive-empathy",
      "assertiveness-skills",
      "conquering-conflict",
      "strengthening-teams-through-communication",
    ],
  },
  {
    slug: "healthcare-developing-people",
    name: "Keep Your Best Nurses",
    industrySlug: "healthcare",
    lane: "developing-people",
    audience: "Unit leaders responsible for retention and staff growth",
    why: "Burnout and travel-agency pay pull good staff out the door; this path helps you coach, recognize, and grow people who choose to stay.",
    courseSlugs: [
      "collaborative-coaching-conversations",
      "developing-others-your-top-priority",
      "the-mentoring-mindset",
      "building-employee-engagement",
      "succession-planning-principles",
    ],
  },
  {
    slug: "healthcare-resilience",
    name: "Steady Under Pressure",
    industrySlug: "healthcare",
    lane: "custom",
    audience: "Clinical leaders facing burnout on their units",
    why: "Codes, short staffing, and moral distress wear crews down over months; this path helps you protect your own steadiness and your team's.",
    courseSlugs: [
      "managing-stress",
      "weathering-the-storm-learning-to-be-resilient",
      "mindfulness-as-a-differentiator",
      "creating-a-culture-of-trust",
      "psychological-safety",
    ],
  },
  {
    slug: "healthcare-patient-experience",
    name: "Patient Experience Starts Here",
    industrySlug: "healthcare",
    lane: "custom",
    audience: "Leaders accountable for HCAHPS scores and service recovery",
    why: "Patients rate the whole visit on how staff treated them; this path connects empathy, service recovery, and team habits to your scores.",
    courseSlugs: [
      "emotional-and-cognitive-empathy",
      "developing-positive-relationships-at-work",
      "connecting-across-cultures",
      "leading-with-an-inclusive-mindset",
      "customer-service-excellence",
    ],
  },
  {
    slug: "logistics-new-leader",
    name: "From Lead to Supervisor",
    industrySlug: "supply-chain-logistics",
    lane: "new-leader",
    audience: "New warehouse and transportation supervisors running a shift",
    why: "Your first peak season as a supervisor comes fast; this path covers the basics of leading a dock crew before volume spikes.",
    courseSlugs: [
      "new-leader-boot-camp",
      "adapting-your-leadership-style",
      "manage-your-time-master-your-life",
      "on-the-spot-development-conversations",
      "driving-results-through-goal-setting",
      "creating-a-culture-of-trust",
    ],
  },
  {
    slug: "logistics-experienced-leader",
    name: "Leading the Whole Network",
    industrySlug: "supply-chain-logistics",
    lane: "experienced-leader",
    audience: "DC managers and regional supply chain leaders",
    why: "Inbound delays, outbound cutoffs, and carrier costs all hit your P&L; this path builds the influence and business sense senior logistics roles demand.",
    courseSlugs: [
      "the-experienced-leader-boot-camp",
      "leading-leaders",
      "servant-leadership-ii-manager-as-servant-leader",
      "the-invincible-influencer",
      "customer-service-excellence",
      "financial-intelligence",
    ],
  },
  {
    slug: "logistics-communication-conflict",
    name: "Straight Talk Across Shifts",
    industrySlug: "supply-chain-logistics",
    lane: "communication-conflict",
    audience: "Supervisors bridging dock, dispatch, and night shift",
    why: "Night shift blames day shift and dispatch blames the dock; this path gives you the feedback and conflict skills to stop the finger-pointing.",
    courseSlugs: [
      "improve-communication-through-the-4p-personality-styles",
      "assertiveness-skills",
      "on-the-spot-development-conversations",
      "conquering-conflict",
      "managing-organizational-conflict",
    ],
  },
  {
    slug: "logistics-developing-people",
    name: "Stop the Turnover Cycle",
    industrySlug: "supply-chain-logistics",
    lane: "developing-people",
    audience: "Leaders losing drivers and warehouse associates too fast",
    why: "Drivers and pickers leave in their first ninety days when nobody invests in them; this path helps you hire, coach, and keep them.",
    courseSlugs: [
      "competency-and-behavioral-based-interviewing",
      "building-employee-engagement",
      "collaborative-coaching-conversations",
      "motivation-models-carrot-or-stick",
      "delegating-for-development",
      "developing-dynamic-teams",
    ],
  },
  {
    slug: "logistics-peak-season",
    name: "Ready for Peak Season",
    industrySlug: "supply-chain-logistics",
    lane: "custom",
    audience: "DC and transportation leaders planning for peak volume",
    why: "Peak turns a steady operation into twelve weeks of overtime and temps; this path helps you plan, prioritize, and hold the crew together.",
    courseSlugs: [
      "balancing-priorities",
      "managing-stress",
      "weathering-the-storm-learning-to-be-resilient",
      "practical-project-management",
      "leading-change",
    ],
  },
  {
    slug: "logistics-carrier-vendor",
    name: "Negotiating with Carriers and Vendors",
    industrySlug: "supply-chain-logistics",
    lane: "custom",
    audience: "Leaders managing carriers, 3PLs, and supplier relationships",
    why: "Capacity is tight and every carrier rate conversation turns into a fight; this path builds the relationship and negotiation skills those talks need.",
    courseSlugs: [
      "building-authentic-relationships",
      "connecting-across-cultures",
      "influence-without-authority",
      "negotiation-for-mutual-success",
    ],
  },
  {
    slug: "construction-new-leader",
    name: "Running the Job Site",
    industrySlug: "construction-engineering",
    lane: "new-leader",
    audience: "Foremen and superintendents new to leading crews",
    why: "You were the best hand on the crew, now you run it, so this path covers giving direction, handling pushback, and keeping the schedule from slipping.",
    courseSlugs: [
      "new-leader-boot-camp",
      "adapting-your-leadership-style",
      "manage-your-time-master-your-life",
      "on-the-spot-development-conversations",
      "conquering-conflict",
      "driving-results-through-goal-setting",
    ],
  },
  {
    slug: "construction-experienced-leader",
    name: "From One Job to Many",
    industrySlug: "construction-engineering",
    lane: "experienced-leader",
    audience: "Project executives and managers who lead other managers",
    why: "When you oversee several supers and a stack of projects, the work shifts from calling plays to building leaders who can call them without you.",
    courseSlugs: [
      "the-experienced-leader-boot-camp",
      "leading-leaders",
      "managing-organizational-conflict",
      "the-invincible-influencer",
      "financial-intelligence",
      "leading-change",
    ],
  },
  {
    slug: "construction-communication-conflict",
    name: "Straight Talk on Site",
    industrySlug: "construction-engineering",
    lane: "communication-conflict",
    audience: "Field and office leaders who handle friction daily",
    why: "Trade friction, change orders, and an owner pushing dates all land on you, so this path builds the nerve and skill to settle things early.",
    courseSlugs: [
      "emotional-triggers",
      "assertiveness-skills",
      "conquering-conflict",
      "negotiation-for-mutual-success",
      "managing-organizational-conflict",
    ],
  },
  {
    slug: "construction-developing-people",
    name: "Building Your Bench",
    industrySlug: "construction-engineering",
    lane: "developing-people",
    audience: "Superintendents and PMs growing the next crew leaders",
    why: "Skilled crews are hard to find and harder to keep, so this path helps you grow foremen from within and give them reasons to stay.",
    courseSlugs: [
      "developing-others-your-top-priority",
      "collaborative-coaching-conversations",
      "delegating-for-development",
      "the-mentoring-mindset",
      "building-employee-engagement",
      "succession-planning-principles",
    ],
  },
  {
    slug: "construction-safety-culture",
    name: "A Culture That Speaks Up",
    industrySlug: "construction-engineering",
    lane: "custom",
    audience: "Safety leads, supers, and anyone accountable for crew safety",
    why: "Near misses get buried when crews fear blame, so this path builds the trust that makes people flag a hazard before it becomes an incident.",
    courseSlugs: [
      "psychological-safety",
      "creating-a-culture-of-trust",
      "emotional-and-cognitive-empathy",
      "ethics-in-the-workplace",
      "strengthening-teams-through-communication",
    ],
  },
  {
    slug: "construction-project-delivery",
    name: "Keeping the Schedule Honest",
    industrySlug: "construction-engineering",
    lane: "custom",
    audience: "Project managers and engineers accountable for delivery",
    why: "RFIs pile up, subs miss dates, and the owner wants answers, so this path sharpens planning, decisions, and the meetings where schedules actually get fixed.",
    courseSlugs: [
      "practical-project-management",
      "balancing-priorities",
      "making-better-decisions-through-critical-thinking",
      "masterfully-managing-meetings",
      "stakeholder-management",
    ],
  },
  {
    slug: "pbs-new-leader",
    name: "From Star to Manager",
    industrySlug: "professional-business-services",
    lane: "new-leader",
    audience: "Newly promoted consultants, accountants, and account leads",
    why: "Your billable hours made you a manager, and now the team's numbers are yours, so this path covers delegating, feedback, and protecting your own time.",
    courseSlugs: [
      "new-leader-boot-camp",
      "maximizing-strengths",
      "delegating-for-development",
      "on-the-spot-development-conversations",
      "balancing-priorities",
    ],
  },
  {
    slug: "pbs-experienced-leader",
    name: "Leading the Practice",
    industrySlug: "professional-business-services",
    lane: "experienced-leader",
    audience: "Partners, practice leads, and directors managing managers",
    why: "Practice leaders juggle utilization targets, partner politics, and people who bill more than they manage, so this path builds the wider view the seat demands.",
    courseSlugs: [
      "the-experienced-leader-boot-camp",
      "leading-leaders",
      "servant-leadership-ii-manager-as-servant-leader",
      "financial-intelligence",
      "fundamentals-of-strategic-planning",
      "leading-change",
    ],
  },
  {
    slug: "pbs-communication-conflict",
    name: "Saying the Hard Thing",
    industrySlug: "professional-business-services",
    lane: "communication-conflict",
    audience: "Client-facing leaders who negotiate scope and manage teams",
    why: "Clients push scope, peers guard accounts, and nobody wants to risk the relationship, so this path helps you say hard things without losing the room.",
    courseSlugs: [
      "emotional-triggers",
      "improve-communication-through-the-4p-personality-styles",
      "assertiveness-skills",
      "conquering-conflict",
      "influence-without-authority",
      "managing-organizational-conflict",
    ],
  },
  {
    slug: "pbs-developing-people",
    name: "Keeping Your Best People",
    industrySlug: "professional-business-services",
    lane: "developing-people",
    audience: "Managers responsible for retention and staff development",
    why: "Your strongest analysts get recruited every quarter, so this path helps you coach them, chart real career paths, and give them a reason to stay.",
    courseSlugs: [
      "collaborative-coaching-conversations",
      "developing-others-your-top-priority",
      "the-mentoring-mindset",
      "professional-pathways-building-a-vision-for-your-career",
      "building-employee-engagement",
    ],
  },
  {
    slug: "pbs-client-trust",
    name: "Winning Client Trust",
    industrySlug: "professional-business-services",
    lane: "custom",
    audience: "Engagement leads and account owners who own renewals",
    why: "Renewals hinge on whether the client trusts your judgment, so this path sharpens how you build relationships, present findings, and handle the scope conversation.",
    courseSlugs: [
      "building-authentic-relationships",
      "customer-service-excellence",
      "stakeholder-management",
      "powerful-presentations",
      "negotiation-for-mutual-success",
    ],
  },
  {
    slug: "pbs-utilization-workload",
    name: "Billable Hours Without Burnout",
    industrySlug: "professional-business-services",
    lane: "custom",
    audience: "Leaders managing utilization, deadlines, and stretched teams",
    why: "Utilization targets and back-to-back engagements wear teams down, so this path helps you protect focus, manage the load, and keep your people steady.",
    courseSlugs: [
      "manage-your-time-master-your-life",
      "balancing-priorities",
      "managing-stress",
      "weathering-the-storm-learning-to-be-resilient",
    ],
  },
  {
    slug: "smb-new-leader",
    name: "Your First Time Managing",
    industrySlug: "small-midsize-businesses",
    lane: "new-leader",
    audience: "First-time supervisors at companies without formal training",
    why: "There's no HR department to call and no formal training, so this path gives you the basics of setting expectations, delegating, and handling performance yourself.",
    courseSlugs: [
      "new-leader-boot-camp",
      "adapting-your-leadership-style",
      "delegating-for-development",
      "on-the-spot-development-conversations",
      "driving-results-through-goal-setting",
      "performance-management",
    ],
  },
  {
    slug: "smb-experienced-leader",
    name: "Running It Without You",
    industrySlug: "small-midsize-businesses",
    lane: "experienced-leader",
    audience: "Owners, GMs, and directors who now manage managers",
    why: "The owner still signs off on everything and growth is outpacing the structure; this path prepares a team of independent leaders.",
    courseSlugs: [
      "the-experienced-leader-boot-camp",
      "leading-leaders",
      "fundamentals-of-strategic-planning",
      "financial-intelligence",
      "leading-change",
    ],
  },
  {
    slug: "smb-communication-conflict",
    name: "Clearing the Air",
    industrySlug: "small-midsize-businesses",
    lane: "communication-conflict",
    audience: "Managers in close-quarters teams where tension spreads fast",
    why: "In a small company you can't avoid anyone, so this path helps you raise the tough issue early instead of letting it sour the whole office.",
    courseSlugs: [
      "emotional-triggers",
      "managing-emotions",
      "improve-communication-through-the-4p-personality-styles",
      "assertiveness-skills",
      "conquering-conflict",
    ],
  },
  {
    slug: "smb-developing-people",
    name: "Growing Talent In-House",
    industrySlug: "small-midsize-businesses",
    lane: "developing-people",
    audience: "Owners and managers building a bench without recruiters",
    why: "You can't outbid bigger firms on salary, so this path helps you coach, engage, and promote from within before your best people start looking elsewhere.",
    courseSlugs: [
      "developing-others-your-top-priority",
      "collaborative-coaching-conversations",
      "the-mentoring-mindset",
      "true-empowerment",
      "building-employee-engagement",
      "succession-planning-principles",
    ],
  },
  {
    slug: "smb-hiring-standards",
    name: "Hiring Without an HR Team",
    industrySlug: "small-midsize-businesses",
    lane: "custom",
    audience: "Owners and managers who hire without HR support",
    why: "One bad hire costs a ten-person company far more than it costs a big one, so this path covers interviewing, onboarding, and holding the standard.",
    courseSlugs: [
      "competency-and-behavioral-based-interviewing",
      "developing-positive-relationships-at-work",
      "creating-a-culture-of-trust",
      "performance-management",
    ],
  },
  {
    slug: "smb-growth-structure",
    name: "Building Structure as You Grow",
    industrySlug: "small-midsize-businesses",
    lane: "custom",
    audience: "Owners and leaders adding people faster than process",
    why: "Growth is outpacing your systems and everything still runs through the owner, so this path builds the planning, priorities, and meeting habits that scale.",
    courseSlugs: [
      "soar-analysis",
      "turn-your-ideas-into-results",
      "practical-project-management",
      "balancing-priorities",
      "masterfully-managing-meetings",
    ],
  },
  {
    slug: "smb-accountability",
    name: "Making It Stick",
    industrySlug: "small-midsize-businesses",
    lane: "custom",
    audience: "Managers who assign work and end up doing it themselves",
    why: "Nothing holds without a clear expectation and a follow-up, so this path builds the habits that keep work owned by the person who took it on.",
    courseSlugs: [
      "driving-results-through-goal-setting",
      "delegating-for-development",
      "on-the-spot-development-conversations",
      "performance-management",
      "creating-a-culture-of-trust",
    ],
  },
  {
    slug: "smb-managing-change",
    name: "Making Change Land",
    industrySlug: "small-midsize-businesses",
    lane: "custom",
    audience: "Managers asked to roll out a change they did not design",
    why: "Change stalls at the manager's door, so this path covers explaining the why, handling pushback, and holding the new way until it sticks.",
    courseSlugs: [
      "leading-change",
      "developing-a-growth-mindset",
      "managing-emotions",
      "influence-without-authority",
      "weathering-the-storm-learning-to-be-resilient",
      "strengthening-teams-through-communication",
    ],
  },
  {
    slug: "mining-new-leader",
    name: "Leading a Mine Crew",
    industrySlug: "mining-natural-resources",
    lane: "new-leader",
    audience: "Shift supervisors newly promoted to lead crews at sites",
    why: "Most crew leaders get promoted off the tools with no training, so this path covers the basics before a long roster tests them.",
    courseSlugs: [
      "new-leader-boot-camp",
      "leadership-essentials-with-a-foundation-of-emotional-intelligence",
      "adapting-your-leadership-style",
      "on-the-spot-development-conversations",
      "conquering-conflict",
      "driving-results-through-goal-setting",
    ],
  },
  {
    slug: "mining-experienced-leader",
    name: "Running Multi-Site Operations",
    industrySlug: "mining-natural-resources",
    lane: "experienced-leader",
    audience: "Superintendents and managers who lead other leaders across sites",
    why: "Site managers juggle production targets, zero-harm standards, and crews they rarely see, so this path builds the strategic reach that scale demands.",
    courseSlugs: [
      "the-experienced-leader-boot-camp",
      "leading-leaders",
      "leading-change",
      "managing-organizational-conflict",
      "financial-intelligence",
    ],
  },
  {
    slug: "mining-communication-conflict",
    name: "Hard Talks on Site",
    industrySlug: "mining-natural-resources",
    lane: "communication-conflict",
    audience: "Supervisors handling friction between crews, contractors, and trades",
    why: "Camp life puts crews together for weeks, so small frictions harden fast; this path gives you the words to sort them early.",
    courseSlugs: [
      "improve-communication-through-the-4p-personality-styles",
      "emotional-triggers",
      "assertiveness-skills",
      "conquering-conflict",
      "managing-organizational-conflict",
      "negotiation-for-mutual-success",
    ],
  },
  {
    slug: "mining-developing-people",
    name: "Building Your Next Crew",
    industrySlug: "mining-natural-resources",
    lane: "developing-people",
    audience: "Leaders building skills and bench strength at resource sites",
    why: "Your most experienced operators are retiring, and the knowledge walks out with them, so this path helps you coach and grow replacements now.",
    courseSlugs: [
      "collaborative-coaching-conversations",
      "the-mentoring-mindset",
      "delegating-for-development",
      "building-employee-engagement",
      "succession-planning-principles",
    ],
  },
  {
    slug: "mining-safety-culture",
    name: "Speaking Up on Safety",
    industrySlug: "mining-natural-resources",
    lane: "custom",
    audience: "Site leaders responsible for zero-harm results and reporting culture",
    why: "Zero-harm only works when people report near misses without fear, so this path builds the trust that makes crews speak up.",
    courseSlugs: [
      "creating-a-culture-of-trust",
      "psychological-safety",
      "emotional-and-cognitive-empathy",
      "ethics-in-the-workplace",
      "making-better-decisions-through-critical-thinking",
      "weathering-the-storm-learning-to-be-resilient",
    ],
  },
  {
    slug: "mining-remote-camps",
    name: "Leading From Fly-In Camps",
    industrySlug: "mining-natural-resources",
    lane: "custom",
    audience: "Leaders managing fly-in crews across rotations and remote sites",
    why: "Fly-in rosters mean you lead people you rarely see off shift, so this path covers connection, handovers, and teams that hold across rotations.",
    courseSlugs: [
      "leading-remote-teams",
      "building-authentic-relationships",
      "strengthening-teams-through-communication",
      "masterfully-managing-meetings",
      "developing-dynamic-teams",
    ],
  },
  {
    slug: "utilities-new-leader",
    name: "Stepping Up to Supervisor",
    industrySlug: "utilities-energy",
    lane: "new-leader",
    audience: "Newly promoted crew leaders and first-time utility supervisors",
    why: "Utilities promote strong technicians into supervision fast, so this path covers the basics before your first storm callout puts them to the test.",
    courseSlugs: [
      "new-leader-boot-camp",
      "leadership-essentials-with-a-foundation-of-emotional-intelligence",
      "adapting-your-leadership-style",
      "delegating-for-development",
      "on-the-spot-development-conversations",
      "driving-results-through-goal-setting",
    ],
  },
  {
    slug: "utilities-experienced-leader",
    name: "Leading Through Grid Change",
    industrySlug: "utilities-energy",
    lane: "experienced-leader",
    audience: "Directors and managers leading other leaders in utility operations",
    why: "Rate cases, aging assets, and a grid mid-transition land on senior leaders, so this path sharpens strategy, influence, and change work at scale.",
    courseSlugs: [
      "the-experienced-leader-boot-camp",
      "leading-leaders",
      "leading-change",
      "the-invincible-influencer",
      "financial-intelligence",
    ],
  },
  {
    slug: "utilities-communication-conflict",
    name: "Straight Talk in Outages",
    industrySlug: "utilities-energy",
    lane: "communication-conflict",
    audience: "Supervisors and managers handling tense field and control-room conversations",
    why: "Storm restoration puts crews, contractors, and mutual aid partners under pressure together, so this path helps you keep hard conversations clean and quick.",
    courseSlugs: [
      "improve-communication-through-the-4p-personality-styles",
      "managing-emotions",
      "assertiveness-skills",
      "conquering-conflict",
      "negotiation-for-mutual-success",
      "managing-organizational-conflict",
    ],
  },
  {
    slug: "utilities-developing-people",
    name: "Passing On the Trade",
    industrySlug: "utilities-energy",
    lane: "developing-people",
    audience: "Leaders coaching apprentices and keeping seasoned crews engaged",
    why: "Half your linemen and operators can retire within five years, so this path helps you mentor, engage, and hold onto what they know.",
    courseSlugs: [
      "collaborative-coaching-conversations",
      "the-mentoring-mindset",
      "developing-others-your-top-priority",
      "building-employee-engagement",
      "succession-planning-principles",
    ],
  },
  {
    slug: "utilities-storm-response",
    name: "Leading Through Storm Response",
    industrySlug: "utilities-energy",
    lane: "custom",
    audience: "Leaders running restoration, mutual aid, and emergency operations",
    why: "Mutual aid crews show up as strangers during a multi-day restoration, so this path prepares you to decide fast and stay steady.",
    courseSlugs: [
      "weathering-the-storm-learning-to-be-resilient",
      "managing-stress",
      "making-better-decisions-through-critical-thinking",
      "balancing-priorities",
      "developing-dynamic-teams",
    ],
  },
  {
    slug: "utilities-public-trust",
    name: "Facing Customers and Regulators",
    industrySlug: "utilities-energy",
    lane: "custom",
    audience: "Leaders facing customers, regulators, and community meetings",
    why: "Every rate increase and prolonged outage gets scrutinized in public, so this path prepares you for customers, regulators, and the rooms in between.",
    courseSlugs: [
      "creating-a-culture-of-trust",
      "customer-service-excellence",
      "stakeholder-management",
      "powerful-presentations",
    ],
  },
  {
    slug: "gov-new-leader",
    name: "Your First Supervisory Role",
    industrySlug: "government-public-sector",
    lane: "new-leader",
    audience: "First-time supervisors in agencies, departments, and public offices",
    why: "You still have the same grade and pay as yesterday, but now you sign the timesheets, so this path covers the first-year basics.",
    courseSlugs: [
      "new-leader-boot-camp",
      "leadership-essentials-with-a-foundation-of-emotional-intelligence",
      "adapting-your-leadership-style",
      "managing-up",
      "on-the-spot-development-conversations",
      "performance-management",
    ],
  },
  {
    slug: "gov-experienced-leader",
    name: "Leading Across Administrations",
    industrySlug: "government-public-sector",
    lane: "experienced-leader",
    audience: "Division chiefs and senior managers who lead other leaders",
    why: "Elected leadership changes and priorities reset, but your staff stays, so this path helps you steer through turnover without losing the mission.",
    courseSlugs: [
      "the-experienced-leader-boot-camp",
      "leading-leaders",
      "leading-change",
      "managing-organizational-conflict",
      "servant-leadership-ii-manager-as-servant-leader",
    ],
  },
  {
    slug: "gov-communication-conflict",
    name: "Hard Conversations, Public Stakes",
    industrySlug: "government-public-sector",
    lane: "communication-conflict",
    audience: "Public sector supervisors handling grievances, pushback, and tough talks",
    why: "Union rules and public records mean every hard conversation leaves a trail, so this path helps you be direct, fair, and clear.",
    courseSlugs: [
      "improve-communication-through-the-4p-personality-styles",
      "emotional-triggers",
      "assertiveness-skills",
      "conquering-conflict",
      "negotiation-for-mutual-success",
      "solid-business-writing",
    ],
  },
  {
    slug: "gov-developing-people",
    name: "Growing Staff Without Promotions",
    industrySlug: "government-public-sector",
    lane: "developing-people",
    audience: "Supervisors keeping good staff when pay bands are fixed",
    why: "Grade levels cap what you can pay, so this path shows how growth, coaching, and real responsibility keep good people from leaving.",
    courseSlugs: [
      "collaborative-coaching-conversations",
      "motivation-models-carrot-or-stick",
      "delegating-for-development",
      "developing-others-your-top-priority",
      "building-employee-engagement",
      "succession-planning-principles",
    ],
  },
  {
    slug: "gov-politics-stakeholders",
    name: "Managing Up and Outward",
    industrySlug: "government-public-sector",
    lane: "custom",
    audience: "Career staff working with appointees, councils, and outside agencies",
    why: "Appointees change and procurement moves slowly, so this path builds the influence you need when you don't control the budget or the calendar.",
    courseSlugs: [
      "navigating-organizational-politics",
      "managing-up",
      "influence-without-authority",
      "stakeholder-management",
      "powerful-presentations",
    ],
  },
  {
    slug: "gov-delivery",
    name: "Delivering Inside Slow Systems",
    industrySlug: "government-public-sector",
    lane: "custom",
    audience: "Program and project leads delivering under public sector constraints",
    why: "Procurement cycles and annual budgets stretch simple work into years, so this path helps you plan, prioritize, and finish inside those limits.",
    courseSlugs: [
      "balancing-priorities",
      "making-better-decisions-through-critical-thinking",
      "practical-project-management",
      "fundamentals-of-strategic-planning",
    ],
  },
];

export function pathsForIndustry(industrySlug: string): LearningPath[] {
  return learningPaths.filter((p) => p.industrySlug === industrySlug);
}

export function pathBySlug(slug: string): LearningPath | undefined {
  return learningPaths.find((p) => p.slug === slug);
}
